import { ChangeDetectionStrategy, Component, computed, linkedSignal, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import { S25FadingSuccessMsgComponent } from "../../s25-fading-success-msg/s25.fading.success.msg.component";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { NameService } from "../../../services/name.service";
import { SE_LOW_ACTIVITY_REPORT } from "./engagement.labels";
import { AtRiskRequest, AtRiskUser, EngagementAtRiskConfig, EngagementService } from "./engagement.service";
import { SeUserEngagementDetailComponent } from "./se.user.engagement.detail.component";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { Table } from "../../s25-table/Table";

@TypeManagerDecorator("s25-ng-se-low-activity")
@Component({
    selector: "s25-ng-se-low-activity",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        S25ButtonComponent,
        S25FadingSuccessMsgComponent,
        S25LoadingInlineStaticComponent,
        S25TableModule,
    ],
    template: `
        <div class="se-low-activity">
            <h2 class="se-low-activity__title">{{ lowActivityReport.displayName }}</h2>
            <div class="se-low-activity__intro">
                <p>
                    This report helps you notice students whose campus engagement has slipped compared with their own
                    recent history. It looks at the kinds of participation your institution tracks in this platform (for
                    example events, check-ins, or other activities your team has chosen to measure) and surfaces
                    students who used to be more active and are now notably quieter.
                </p>
                <p>
                    <strong>Recent window (days)</strong> is the last stretch of days ending today. For example, with 14
                    days, the system looks only at that most recent two weeks. For each student it computes an
                    <em>average per day</em> over that window (how much they participated per day in that period).
                </p>
                <p>
                    <strong>Baseline window (days)</strong> is a separate block of time that comes
                    <em>right before</em> the recent window, and it does <em>not</em> overlap the recent days. Example:
                    recent 14 days and baseline 28 days means the baseline is the 28 days immediately before those 14.
                    Together that covers 42 calendar days end-to-end, but the math uses two averages: one for the older
                    28-day block, one for the newest 14-day block. Only students who had some activity during that
                    baseline block are included, so you are not flagging someone who was inactive both then and now.
                </p>
                <p>
                    <strong>Drop threshold (%)</strong> compares those two averages per student. If the recent average
                    is at least that much lower than the baseline average (expressed as a percent drop), the student
                    appears on the list. A higher percentage means fewer names but stronger signals of change; a lower
                    percentage catches smaller dips.
                </p>
                <p>
                    Use <strong>View</strong> to try settings without saving. Use <strong>Save</strong> to keep your
                    chosen windows and threshold as the defaults for this report next time.
                </p>
            </div>
            <s25-ng-fading-success-msg [(message)]="saveSuccessMessage" [durationInMS]="4000" />

            @if (configResource.resource.error()) {
                <p class="se-low-activity__error">{{ configResource.resource.error()!.message }}</p>
            }

            @if (configResource.resource.isLoading() && !configResource.lastValue()) {
                <s25-ng-loading-inline-static [text]="'Loading configuration…'" />
            } @else if (configResource.resource.hasValue()) {
                <div class="se-low-activity__form">
                    <label class="se-low-activity__field">
                        <span class="se-low-activity__label">Recent window (days) <span class="required">*</span></span>
                        <input
                            type="number"
                            class="c-input"
                            min="1"
                            required
                            [ngModel]="draft().recentWindowDays"
                            (ngModelChange)="patchDraft({ recentWindowDays: coercePositiveInt($event, 1) })"
                        />
                    </label>
                    <label class="se-low-activity__field">
                        <span class="se-low-activity__label"
                            >Baseline window (days) <span class="required">*</span></span
                        >
                        <input
                            type="number"
                            class="c-input"
                            min="1"
                            required
                            [ngModel]="draft().baselineWindowDays"
                            (ngModelChange)="patchDraft({ baselineWindowDays: coercePositiveInt($event, 1) })"
                        />
                    </label>
                    <label class="se-low-activity__field">
                        <span class="se-low-activity__label">Drop threshold (%) <span class="required">*</span></span>
                        <input
                            type="number"
                            class="c-input"
                            min="0"
                            max="100"
                            required
                            [ngModel]="draft().dropThresholdPct"
                            (ngModelChange)="patchDraft({ dropThresholdPct: coercePct($event) })"
                        />
                    </label>
                    <div class="se-low-activity__actions">
                        <s25-ng-button
                            [type]="'outline'"
                            [buttonText]="'View'"
                            [onClick]="onPreview"
                            [disabled]="atRiskPreview.resource.isLoading()"
                        />
                        <s25-ng-button
                            [type]="'primary'"
                            [buttonText]="'Save'"
                            [onClick]="onSaveDefaults"
                            [isLoading]="saving()"
                            [disabled]="saving()"
                        />
                    </div>
                </div>

                @if (atRiskPreview.resource.error()) {
                    <p class="se-low-activity__error">{{ atRiskPreview.resource.error()!.message }}</p>
                }

                @if (previewGeneration() > 0) {
                    @if (atRiskPreview.resource.isLoading()) {
                        <s25-ng-loading-inline-static [text]="'Loading preview…'" />
                    } @else {
                        <s25-ng-table
                            [caption]="lowActivityReport.displayName"
                            [unlimitedWidth]="true"
                            [columnSortable]="true"
                            [dataSource]="previewTableConfig"
                            [hasExport]="true"
                            [hasRefresh]="true"
                            [exportFilename]="exportXlsxFilename"
                            [exportSheetName]="lowActivityReport.displayName"
                            [showHeaderWhenNoData]="true"
                            [hasExpandableRows]="true"
                        />
                    }
                }
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .se-low-activity__title {
                margin: 0 0 1rem;
            }
            .se-low-activity__intro {
                max-width: 42rem;
                margin: 0 0 1.25rem;
                line-height: 1.5;
                color: var(--color--text-strong);
            }
            .se-low-activity__intro p {
                margin: 0 0 0.75rem;
            }
            .se-low-activity__intro p:last-child {
                margin-bottom: 0;
            }
            .se-low-activity__form {
                display: grid;
                gap: 1rem;
                max-width: 28rem;
                margin-bottom: 1.5rem;
            }
            .se-low-activity__field {
                display: grid;
                gap: 0.35rem;
            }
            .se-low-activity__label {
                font-weight: 600;
            }
            .required {
                color: var(--color--danger);
            }
            .se-low-activity__actions {
                display: flex;
                flex-wrap: wrap;
                gap: 0.75rem;
            }
            .se-low-activity__error {
                color: var(--color--danger);
                margin: 0 0 1rem;
            }
        `,
    ],
})
export class SeLowActivityComponent {
    readonly lowActivityReport = SE_LOW_ACTIVITY_REPORT;

    readonly saveSuccessMessage = signal("");
    readonly saving = signal(false);

    readonly configResource = s25Resource(() => EngagementService.getAtRiskConfig());

    readonly draft = linkedSignal<Required<AtRiskRequest>>(() => {
        const cfg = this.configResource.value();
        return {
            recentWindowDays: cfg?.recentWindowDays ?? 14,
            baselineWindowDays: cfg?.baselineWindowDays ?? 28,
            dropThresholdPct: cfg?.dropThresholdPct ?? 20,
        };
    });

    readonly previewGeneration = signal(1);

    readonly atRiskPreview = s25Resource({
        if: () => this.previewGeneration() > 0,
        params: () => ({
            gen: this.previewGeneration(),
            recentWindowDays: this.draft().recentWindowDays,
            baselineWindowDays: this.draft().baselineWindowDays,
            dropThresholdPct: this.draft().dropThresholdPct,
        }),
        loader: async ({ params }) => {
            const rows = await EngagementService.getAtRiskUsers({
                recentWindowDays: params.recentWindowDays,
                baselineWindowDays: params.baselineWindowDays,
                dropThresholdPct: params.dropThresholdPct,
            } satisfies AtRiskRequest);
            const users = !rows?.length ? [] : [...rows].sort((a, b) => b.dropPct - a.dropPct);
            const nameMap =
                users.length > 0
                    ? await NameService.getNameMap(
                          "contacts",
                          users.map((u) => u.contId),
                      )
                    : new Map<number, string>();
            return { users, nameMap };
        },
    });

    readonly sortedAtRiskUsers = computed((): AtRiskUser[] => this.atRiskPreview.value()?.users ?? []);

    readonly nameMap = computed(() => this.atRiskPreview.value()?.nameMap ?? new Map<number, string>());

    private readonly loadPreviewTableRows = async (): Promise<Table.DataSourceResponse> => {
        const users = this.sortedAtRiskUsers();
        const nm = this.nameMap();
        const rows: Table.Row[] = users.map((row) => ({
            id: String(row.contId),
            name: String(row.contId),
            expandable: true,
            expandedContent: {
                component: SeUserEngagementDetailComponent,
                inputs: { contId: row.contId },
            },
            cells: {
                name: { text: nm?.get(row.contId) ?? String(row.contId) },
                recentRate: this.textCellWithStringSort(this.formatRate(row.recentRate), row.recentRate),
                baselineRate: this.textCellWithStringSort(this.formatRate(row.baselineRate), row.baselineRate),
                dropPct: this.textCellWithStringSort(this.formatPct(row.dropPct), row.dropPct),
            },
            data: row,
        }));
        return { rows };
    };

    readonly previewTableConfig: Table.DataSource = {
        type: "unpaginated",
        columns: [
            { id: "name", header: "Name", export: true },
            { id: "recentRate", header: "Recent rate", export: true },
            { id: "baselineRate", header: "Baseline rate", export: true },
            { id: "dropPct", header: "% drop", export: true },
        ],
        dataSource: this.loadPreviewTableRows,
    };

    readonly exportXlsxFilename = `${SE_LOW_ACTIVITY_REPORT.exportFileBasename}.xlsx`;

    patchDraft(partial: Partial<Required<AtRiskRequest>>): void {
        this.draft.update((d) => ({ ...d, ...partial }));
    }

    coercePositiveInt(value: string | number | null, fallback: number): number {
        const n = Number(value);
        if (!Number.isFinite(n) || n < 1) {
            return fallback;
        }
        return Math.floor(n);
    }

    coercePct(value: string | number | null): number {
        const n = Number(value);
        if (!Number.isFinite(n)) {
            return 0;
        }
        return Math.min(100, Math.max(0, n));
    }

    formatRate(n: number): string {
        return Number.isFinite(n) ? n.toFixed(4) : "—";
    }

    private textCellWithStringSort(text: string, sortKey: number): Table.Cell {
        return { text, sortValue: String(sortKey) };
    }

    formatPct(n: number): string {
        return Number.isFinite(n) ? `${n.toFixed(2)}%` : "—";
    }

    onPreview = (): void => {
        this.saveSuccessMessage.set("");
        this.previewGeneration.update((g) => g + 1);
    };

    onSaveDefaults = async (): Promise<void> => {
        const cfg = this.configResource.value();
        if (!cfg) {
            return;
        }
        const d = this.draft();
        this.saving.set(true);
        try {
            const payload: EngagementAtRiskConfig = {
                instance: cfg.instance,
                recentWindowDays: d.recentWindowDays,
                baselineWindowDays: d.baselineWindowDays,
                dropThresholdPct: d.dropThresholdPct,
            };
            await EngagementService.updateAtRiskConfig(payload);
            this.saveSuccessMessage.set("Defaults saved.");
            this.configResource.resource.reload();
        } catch (e) {
            S25Util.showError(e);
        } finally {
            this.saving.set(false);
        }
    };
}
